/**
 * The shared "write something back to IT" form.
 *
 * A comment and an answer to an information request are the same interaction
 * with different copy, so they share one component: a `Form` + `Field` +
 * autosizing `Textarea`, client-side length validation with translated
 * messages, and a disabled submit while the mutation is in flight.
 *
 * Note the submit prop is `onFormSubmit`, not `onSubmit` — the design system
 * `Form` calls `preventDefault()` on the native event when it is set.
 *
 * ## Attachments
 *
 * Opt-in, through {@link ComposeFormProps.attachments}. Omitting it leaves the
 * form exactly as text-only as it was, and that escape hatch is load-bearing
 * rather than stylistic: a surface only gets the uploader when the wire can
 * actually carry what it collects. Offering one that cannot is worse than
 * offering none, because the user watches a chip appear and concludes the file
 * was sent. All three consumers can carry files today, but the resolution panel
 * reaches this form on one of its two paths only — accepting a resolution
 * writes no journal comment, so the server refuses `attachment_ids` outright
 * there (see `ticket-resolution-panel.tsx`).
 *
 * The uploader is the shared one, so everything it already does — the single
 * upload path, the server-side allowlist, drag-and-drop, the app-wide guard
 * against a missed drop — arrives here for free rather than being reimplemented
 * per surface.
 *
 * **The text stays required even when a file is attached.** Not a UI
 * preference: `TicketCommentCreateRequest.body` and
 * `TicketInfoAnswerRequest.body` are both `min_length=1` server-side, so a
 * bodiless "here's the screenshot" is a 422 whatever this form does. Rather
 * than let someone discover that by pressing Send, the empty-box message
 * changes when a file is waiting — it asks for the one line of context that
 * makes the file actionable instead of repeating "write something". Inventing
 * body text on the user's behalf is not an option: §3.6 stores these writes
 * verbatim as the Ivanti journal, and a sentence they did not type has no
 * business in an audit trail.
 */

import { Button, Field, Form, Textarea, Typography } from "@norican/react";
import { type ReactNode, useState } from "react";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import { ErrorState, HighlightedField } from "@/components/common";
import { useT } from "@/i18n";
import { useFieldHighlight } from "@/lib/field-highlight";

import { AttachmentNudgePrompt, useAttachmentNudge } from "./attachment-nudge";

/** The contract caps every free-text write field at 4000 characters. */
export const MAX_BODY_LENGTH = 4000;

/** Turns the uploader on, and carries the copy that frames it. */
export interface ComposeFormAttachments {
  /** Label for the uploader's field, e.g. "Screenshot or document". */
  label: string;
  /** The sentence under it. This is where a surface argues for a file. */
  hint?: string;
  /**
   * Ask once, before an empty-handed send, whether there is a file.
   *
   * Reserved for the surfaces where IT is *waiting* on something specific. On
   * an ordinary comment it is noise, and a prompt people learn to dismiss stops
   * working on the surface that needed it.
   */
  nudge?: boolean;
}

export interface ComposeFormProps {
  name: string;
  label: string;
  placeholder?: string;
  hint?: string;
  submitLabel: string;
  /**
   * Resolves when the write succeeded; rejects to keep the draft on screen.
   *
   * `attachmentIds` is always passed and is empty when this form has no
   * uploader, so a text-only caller can keep ignoring the second argument.
   */
  onSubmit: (body: string, attachmentIds: string[]) => Promise<unknown>;
  /** Omit to keep the form text-only. */
  attachments?: ComposeFormAttachments;
  pending?: boolean;
  /** The rejection from the mutation, rendered inline above the control. */
  error?: unknown;
  /** Extra controls beside the submit button (e.g. a "not fixed" secondary). */
  secondaryAction?: ReactNode;
  rows?: number;
  autoFocus?: boolean;
}

export function ComposeForm({
  name,
  label,
  placeholder,
  hint,
  submitLabel,
  onSubmit,
  attachments,
  pending = false,
  error,
  secondaryAction,
  rows = 3,
  autoFocus = false,
}: ComposeFormProps) {
  const { t } = useT("tickets");
  const [body, setBody] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  /* Bumping this remounts the uploader, which is how it is cleared — it owns
     its files and never takes an id list back as a prop. Same mechanism the
     composer uses on send. */
  const [uploaderKey, setUploaderKey] = useState(0);
  /* Harmless on the surfaces that opted out of attachments entirely: with no
     uploader mounted the hook's ref is null and the paste is left alone. */
  const paste = usePasteToAttach({ disabled: pending });

  const nudge = useAttachmentNudge(attachmentIds.length > 0);

  const handleSubmit = () => {
    const trimmed = body.trim();
    if (trimmed.length === 0) {
      /* A file with no words is the case worth spending a distinct message on.
         The server rejects it either way (`body` is `min_length=1`), but
         "please write something" reads like the form ignored the file that is
         plainly sitting there in a chip above the button. */
      setValidationError(
        attachmentIds.length > 0
          ? t("validation.bodyRequiredWithFile")
          : t("validation.bodyRequired"),
      );
      return;
    }
    if (trimmed.length > MAX_BODY_LENGTH) {
      setValidationError(t("validation.bodyTooLong", { max: MAX_BODY_LENGTH }));
      return;
    }
    setValidationError(null);

    // Only where the surface opted in — see `ComposeFormAttachments.nudge`.
    if (attachments?.nudge === true && nudge.hold()) {
      return;
    }

    onSubmit(trimmed, attachmentIds).then(
      () => {
        /* The field stays editable while sending. Clear it only if it still
           holds what was sent; anything typed meanwhile is a new draft. */
        setBody((current) => (current.trim() === trimmed ? "" : current));
        setAttachmentIds([]);
        setUploading(false);
        setUploaderKey((key) => key + 1);
      },
      () => undefined, // The mutation's own error state renders below.
    );
  };

  const remaining = MAX_BODY_LENGTH - body.length;
  // Field-state tint (5 Oct): nothing is prefilled here; yellow while empty.
  const bodyState = useFieldHighlight(null, body, true);
  const counterId = `${name}-counter`;
  const attachmentsId = `${name}-attachments`;
  // An upload still in flight has no id yet, so sending through it would post
  // the comment without the file the user is waiting on.
  const holdSubmit = pending || uploading;

  return (
    <Form className="flex flex-col gap-3" onFormSubmit={handleSubmit} onPaste={paste.onPaste}>
      {error === undefined || error === null ? null : (
        <ErrorState variant="inline" error={error} offerHelpdeskContact />
      )}

      <HighlightedField state={bodyState}>
        <Field name={name} label={label} hint={hint} error={validationError ?? undefined} required>
          <Textarea
            name={name}
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              /* The Form refuses to submit while the field carries an error, and
               this handler is the only other place that clears it — without
               it, a reply first sent empty could never be sent at all. */
              const next = event.target.value.trim();
              if (next.length > 0 && next.length <= MAX_BODY_LENGTH) {
                setValidationError(null);
              }
            }}
            placeholder={placeholder}
            rows={rows}
            autoResize
            autoFocus={autoFocus}
            maxLength={MAX_BODY_LENGTH}
            aria-describedby={counterId}
          />
        </Field>
      </HighlightedField>

      {attachments === undefined ? null : (
        <Field
          name={attachmentsId}
          label={attachments.label}
          hint={attachments.hint}
          disabled={pending}
        >
          <AttachmentUploader
            ref={paste.ref}
            key={uploaderKey}
            id={attachmentsId}
            onChange={setAttachmentIds}
            onPendingChange={setUploading}
            disabled={pending}
          />
        </Field>
      )}

      {attachments?.nudge === true && nudge.visible ? (
        <AttachmentNudgePrompt submitLabel={submitLabel} />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Typography id={counterId} variant="caption" tone="muted" aria-live="polite">
          {uploading
            ? t("attachment.uploading")
            : t("validation.charactersLeft", { count: remaining })}
        </Typography>
        <div className="flex flex-wrap items-center gap-2">
          {secondaryAction}
          <Button type="submit" loading={holdSubmit} disabled={holdSubmit}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </Form>
  );
}
